/**
 * AITextArea - Textarea with AI Polish and Voice Transcription buttons
 *
 * Drop-in replacement for `<textarea>` that adds AI-powered polish and
 * voice-to-text buttons in the label row. Designed for app-wide use in
 * any form field where users type substantial text.
 *
 * @example
 * ```tsx
 * <AITextArea
 *   label="Scope of Work"
 *   value={scope}
 *   onChange={(e) => setScope(e.target.value)}
 *   rows={5}
 *   enablePolish
 *   enableVoice
 * />
 * ```
 *
 * @param enablePolish - Show AI Polish button (default: true)
 * @param enableVoice - Show Voice Input button (default: true)
 * @param polishTone - Tone for polish: 'professional' | 'technical' | 'casual'
 * @param polishPrompt - Custom instructions for the AI polisher
 * @param fieldContext - Label context passed to AI for better results
 */

import React, { Suspense, useCallback, useState, useRef, useEffect } from 'react';

const AIPolishButton = React.lazy(
  () => import('../../forms/public/fields/AIPolishButton').then(m => ({ default: m.AIPolishButton }))
);
const VoiceInputButton = React.lazy(
  () => import('../../forms/public/fields/VoiceInputButton').then(m => ({ default: m.VoiceInputButton }))
);
const AIPolishModal = React.lazy(
  () => import('../../forms/public/fields/AIPolishModal').then(m => ({ default: m.AIPolishModal }))
);

export interface AITextAreaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> {
  /** Field label displayed above the textarea */
  label?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Helper text shown below the textarea */
  helperText?: string;
  /** Error message - replaces helper text and styles the border red */
  error?: string;
  /** Show AI Polish button (default: true) */
  enablePolish?: boolean;
  /** Show Voice Input button (default: true) */
  enableVoice?: boolean;
  /** Tone for AI polishing */
  polishTone?: 'professional' | 'technical' | 'casual';
  /** Custom prompt for the AI polisher */
  polishPrompt?: string;
  /** Context label passed to AI (defaults to label prop) */
  fieldContext?: string;
  /** onChange handler - uses standard textarea event signature */
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  /** Alternative: direct value change callback (simpler for some use cases) */
  onValueChange?: (value: string) => void;
}

export function AITextArea({
  label,
  required = false,
  helperText,
  error,
  enablePolish = true,
  enableVoice = true,
  polishTone = 'professional',
  polishPrompt,
  fieldContext,
  onChange,
  onValueChange,
  value = '',
  disabled,
  className,
  ...textareaProps
}: AITextAreaProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [polishModal, setPolishModal] = useState<{
    isOpen: boolean;
    original: string;
    polished: string;
  }>({ isOpen: false, original: '', polished: '' });

  const currentValue = String(value);
  const context = fieldContext || label || '';

  // Dispatch a synthetic change event so controlled forms work
  const updateValue = useCallback(
    (newValue: string) => {
      if (onValueChange) {
        onValueChange(newValue);
        return;
      }
      if (textareaRef.current && onChange) {
        // Use native setter to trigger React's synthetic event system
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
          window.HTMLTextAreaElement.prototype,
          'value'
        )?.set;
        nativeInputValueSetter?.call(textareaRef.current, newValue);
        const event = new Event('input', { bubbles: true });
        textareaRef.current.dispatchEvent(event);
      }
    },
    [onChange, onValueChange]
  );

  const handlePolishSuccess = useCallback(
    (polished: string, original: string) => {
      setPolishModal({ isOpen: true, original, polished });
    },
    []
  );

  const handlePolishUseText = useCallback(
    (text: string) => {
      updateValue(text);
      setPolishModal({ isOpen: false, original: '', polished: '' });
    },
    [updateValue]
  );

  const handleVoiceTranscription = useCallback(
    (newValue: string) => {
      updateValue(newValue);
    },
    [updateValue]
  );

  // Auto-grow textarea based on content
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea && !textareaProps.rows) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.max(textarea.scrollHeight, 80)}px`;
    }
  }, [currentValue, textareaProps.rows]);

  const showAIButtons = (enablePolish || enableVoice) && !disabled;

  return (
    <div className="w-full">
      {/* Label only. The AI buttons used to share this row, which meant a field
          with no label still rendered a full-height row containing an empty
          spacer and two icons — a whole line of vertical space spent on two
          buttons. They now sit beside the field itself (below), so an unlabelled
          AITextArea costs exactly the height of its textarea.
          Moved 2026-09-18 (Dillan), for every caller rather than per-module. */}
      {label && (
        <label className="mb-1.5 block text-sm font-medium text-slate-700">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      {/* Textarea, with the AI buttons inline beside it. `items-end` keeps them
          pinned to the bottom edge as the textarea auto-grows, so they stay put
          rather than drifting down the side of a long note. */}
      <div className="flex w-full items-end gap-1.5">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={onValueChange
            ? (e) => onValueChange(e.target.value)
            : onChange
          }
          disabled={disabled}
          className={`
            flex-1 min-w-0 px-3 py-2 border rounded-lg text-sm
            focus:outline-none focus:ring-2 focus:border-transparent resize-y
            min-h-[44px]
            ${error
              ? 'border-red-500 focus:ring-red-200'
              : 'border-slate-300 focus:ring-blue-200 focus:border-blue-500'
            }
            ${disabled ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : 'bg-white'}
            ${className || ''}
          `}
          {...textareaProps}
        />

        {showAIButtons && (
          <div className="flex shrink-0 items-center gap-1 pb-0.5">
            <Suspense fallback={null}>
              {enablePolish && (
                <AIPolishButton
                  fieldContext={context}
                  customPrompt={polishPrompt}
                  tone={polishTone}
                  currentValue={currentValue}
                  onSuccess={handlePolishSuccess}
                  disabled={disabled}
                />
              )}
              {enableVoice && (
                <VoiceInputButton
                  fieldContext={context}
                  currentValue={currentValue}
                  onValueChange={handleVoiceTranscription}
                  disabled={disabled}
                />
              )}
            </Suspense>
          </div>
        )}
      </div>

      {/* Helper / Error text */}
      {helperText && !error && (
        <p className="mt-1 text-sm text-slate-500">{helperText}</p>
      )}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}

      {/* Polish Modal */}
      {polishModal.isOpen && (
        <Suspense fallback={null}>
          <AIPolishModal
            isOpen={polishModal.isOpen}
            originalText={polishModal.original}
            polishedText={polishModal.polished}
            fieldLabel={context}
            customPrompt={polishPrompt}
            tone={polishTone}
            enableRetry
            onClose={() => setPolishModal({ isOpen: false, original: '', polished: '' })}
            onUseText={handlePolishUseText}
          />
        </Suspense>
      )}
    </div>
  );
}
